import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { USERNAME_CHAR_LIMIT } from '../../../../../../corelogic/domain/constants/fieldLimits.constants';
import { UserProfile } from '../../../../../../corelogic/domain/models/user.model';
import { ValidationResult } from '../../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { store } from '../../../../../../corelogic/state/store';
import { UPDATE_USER_PROFILE_SUCCESS } from '../../../../../../corelogic/state/user/user.action.type';
import { SecureTextField } from '../../Shared/SecureTextField/SecureTextField';
import { SuccessModal } from '../../Shared/SuccessModal/SuccessModal';

interface UsernameEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUsername: string;
  userProfile: UserProfile | null;
  token: string | null;
}

export const UsernameEditModal: React.FC<UsernameEditModalProps> = ({
  isOpen,
  onClose,
  currentUsername,
  userProfile,
  token,
}) => {
  const [username, setUsername] = useState(currentUsername);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingUsernameUpdate, setPendingUsernameUpdate] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  // Unified security validation state (matches established pattern)
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});

  // Create a debounced function to check username availability
  const checkUsernameAvailability = useCallback(
    debounce(async (username: string) => {
      if (username.trim().length < 3 || username === currentUsername) {
        setIsAvailable(null);
        return;
      }

      setIsChecking(true);
      try {
        // This will need to be implemented to call your backend API
        // const response = await checkUsernameUnique(username);
        // setIsAvailable(response.available);

        // Mock implementation for now
        await new Promise((resolve) => setTimeout(resolve, 800));
        // Randomly set available for testing
        setIsAvailable(Math.random() > 0.3);
      } catch (error) {
        setError('Could not check username availability');
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    }, 500),
    [currentUsername],
  );

  // Generic secure change handler (matches established pattern)
  const handleSecureChange = (
    field: string,
    value: string,
    validation: ValidationResult,
  ) => {
    // Update form values
    switch (field) {
      case 'username':
        setUsername(value);
        break;
    }

    // Update validation state
    setFieldValidations((prev) => ({ ...prev, [field]: validation }));
    setError('');

    // Check availability if it's different from current and meets length requirement
    if (value !== currentUsername && value.trim().length >= 3) {
      checkUsernameAvailability(value);
    } else {
      setIsAvailable(null);
    }
  };

  // Check if there are any high-severity security issues (matches established pattern)
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  const validateUsername = () => {
    if (!username.trim()) {
      setError('Username cannot be empty');
      return false;
    }
    if (username.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }
    if (username === currentUsername) {
      setError('New username must be different from current one');
      return false;
    }
    if (isAvailable === false) {
      setError('This username is already taken');
      return false;
    }
    if (isAvailable === null && username !== currentUsername) {
      setError('Please wait for username availability check');
      return false;
    }

    // Security issues are already checked in button disabled state
    return true;
  };

  const handleSubmit = async () => {
    if (!validateUsername()) return;

    setIsSubmitting(true);
    setPendingUsernameUpdate(true);

    try {
      // This will need more work backend API route to implement
      /*await store.dispatch(
        updateUserProfile({
          token,
          currentProfile: userProfile,
          newData: { username },
          retry: 0
        })
      );*/
    } catch (error) {
      setError('Failed to update username. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Listen for successful change of username to show success modal
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (
        action.type === UPDATE_USER_PROFILE_SUCCESS &&
        pendingUsernameUpdate
      ) {
        setShowSuccessModal(true);
        onClose();
        setPendingUsernameUpdate(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingUsernameUpdate]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={!isSubmitting ? onClose : undefined}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '450px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Rubik Mono One',
            fontSize: '1.1rem',
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 1,
          }}
        >
          Change Username
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Choose a new username that will be visible to others.
          </Typography>

          <SecureTextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={username}
            onChange={(value, validation) =>
              handleSecureChange('username', value, validation)
            }
            fieldType="username"
            fieldName="Username"
            error={!!error || isAvailable === false}
            showCharacterCounter={true}
            helperText={
              error
                ? error
                : isChecking
                  ? 'Checking availability...'
                  : isAvailable === true
                    ? 'Username is available!'
                    : isAvailable === false
                      ? 'Username is already taken'
                      : ''
            }
            disabled={isSubmitting}
            inputProps={{ maxLength: USERNAME_CHAR_LIMIT }}
            InputProps={{
              sx: { color: 'white' },
              endAdornment:
                username !== currentUsername &&
                username.trim().length >= 3 &&
                (isChecking ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : isAvailable === true ? (
                  <CheckCircleIcon color="success" />
                ) : isAvailable === false ? (
                  <ErrorIcon color="error" />
                ) : null),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#cc0000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#cc0000',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={onClose}
            sx={{ color: 'white' }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Tooltip title="Coming Soon!" arrow>
            <span>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={true}
                sx={{
                  backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Save'
                )}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Username Updated"
        message="Your username has been successfully updated."
        autoHideDuration={3000}
      />
    </>
  );
};
